import { useEffect, useState, useRef } from 'react';
import { type GraphQLSubscriptionConfig, type OperationType } from 'relay-runtime';

import { useSubscriptionSkippable } from './useSubscriptionSkippable';
import { useSocketContext } from '../SocketContext';

type UseSubscriptionRefetchOptions = {
    refetch: () => void; // should be wrapped in useCallback
    skip?: boolean;
    refetchOnReconnect?: boolean;
    maxReconnectRefetches?: number;
    pollingInterval?: number;
    pollWhileDisconnected?: boolean;
    alwaysPoll?: boolean;
    maxPollingRefetches?: number;
};
type UseSubscriptionRefetchReturn = {
    isConnected: boolean;
    isOnline: boolean;
    isPolling: boolean;
    reconnectRefetches: number;
    hadError: boolean;
};

const DEFAULT_POLLING_INTERVAL = 2 * 60 * 1000; // 2m

// adds refetches to a subscription to make it more robust
export function useSubscriptionRefetch<TSubscriptionPayload extends OperationType>(
    config: GraphQLSubscriptionConfig<TSubscriptionPayload>, // should be wrapped in useMemo
    options: UseSubscriptionRefetchOptions
): UseSubscriptionRefetchReturn {
    const {
        refetch,
        skip = false,
        refetchOnReconnect = true,
        maxReconnectRefetches = 15,
        pollingInterval = DEFAULT_POLLING_INTERVAL,
        pollWhileDisconnected = false,
        alwaysPoll = false,
        maxPollingRefetches = 30,
    } = options;

    const [isConnected, setIsConnected] = useState(false);
    const [hadError, setHadError] = useState(false);
    const [isPolling, setIsPolling] = useState(alwaysPoll);
    const [isOnline, setIsOnline] = useState(window.navigator.onLine);
    const reconnectRefetches = useRef(0);
    const pollingRefetches = useRef(0);

    const { socketListeners } = useSocketContext();
    if (!socketListeners) {
        throw new Error(
            'SocketContextProvider must be present in the react tree with socketListeners initialized'
        );
    }

    useSubscriptionSkippable(config, skip);

    useEffect(() => {
        if (skip) {
            return () => {};
        }
        const disposeConnected = socketListeners.connected(() => {
            setIsConnected(true);
        });
        const disposeError = socketListeners.error(() => {
            setIsConnected(false);
            setHadError(true);
        });
        function updateOnlineStatus(): void {
            setIsOnline(window.navigator.onLine);
        }
        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);
        return () => {
            disposeConnected();
            disposeError();
            window.removeEventListener('online', updateOnlineStatus);
            window.removeEventListener('offline', updateOnlineStatus);
        };
    }, [skip, socketListeners]);

    // refetch on reconnect
    useEffect(() => {
        if (
            refetchOnReconnect &&
            reconnectRefetches.current < maxReconnectRefetches &&
            isConnected &&
            hadError &&
            !skip
        ) {
            reconnectRefetches.current += 1;
            refetch();
        }
    }, [isConnected, hadError, refetch, refetchOnReconnect, skip, maxReconnectRefetches]);

    // poll when disconnected
    useEffect(() => {
        if (!pollWhileDisconnected || alwaysPoll || skip) {
            return;
        }
        if (!isConnected && isOnline) {
            setIsPolling(true);
        } else if (isConnected) {
            setIsPolling(false);
        }
    }, [isConnected, pollWhileDisconnected, alwaysPoll, isOnline, skip]);

    // stop polling when offline, resume polling when back online if alwaysPoll
    useEffect(() => {
        if (!isOnline) {
            setIsPolling(false);
        } else if (isOnline && alwaysPoll) {
            setIsPolling(true);
        }
    }, [isOnline, alwaysPoll]);

    useEffect(() => {
        if (skip) {
            return () => {};
        }
        let intervalId: undefined | number;
        if (isPolling && pollingRefetches.current < maxPollingRefetches) {
            intervalId = window.setInterval(() => {
                if (pollingRefetches.current < maxPollingRefetches && !skip) {
                    pollingRefetches.current += 1;
                    refetch();
                } else {
                    clearInterval(intervalId);
                }
            }, pollingInterval);
        }
        return () => {
            clearInterval(intervalId);
        };
    }, [isPolling, pollingInterval, refetch, skip, maxPollingRefetches]);

    return {
        isConnected,
        isOnline,
        isPolling,
        reconnectRefetches: reconnectRefetches.current,
        hadError,
    };
}
